import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { CheckCircle2, AlertTriangle, Info, OctagonX, X } from 'lucide-react';
const MAX_VISIBLE = 4;
const DEFAULT_DURATION_MS = 5000;
const ToastContext = createContext(null);
let nextId = 0;
function makeId() {
    nextId += 1;
    return `toast-${String(nextId)}-${String(Math.random()).slice(2, 8)}`;
}
/**
 * Mount this once near the root (App.tsx) inside a `<ToastProvider>` or
 * — most commonly — as a standalone provider with `<Toaster>` as the
 * portal target. Children render below the toast container so consumers
 * can wrap their entire tree without a second provider.
 */
export function Toaster({ maxVisible = MAX_VISIBLE, children }) {
    const [toasts, setToasts] = useState([]);
    const timersRef = useRef(new Map());
    const dismiss = useCallback((id) => {
        const t = timersRef.current.get(id);
        if (t !== undefined) {
            clearTimeout(t);
            timersRef.current.delete(id);
        }
        setToasts((prev) => prev.filter((x) => x.id !== id));
    }, []);
    const toast = useCallback((input) => {
        const id = makeId();
        const duration = input.durationMs ?? DEFAULT_DURATION_MS;
        const entry = { ...input, id, startedAt: Date.now() };
        setToasts((prev) => [...prev, entry].slice(-maxVisible));
        if (duration > 0) {
            const handle = setTimeout(() => dismiss(id), duration);
            timersRef.current.set(id, handle);
        }
        return id;
    }, [dismiss, maxVisible]);
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            for (const t of timers.values())
                clearTimeout(t);
            timers.clear();
        };
    }, []);
    const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, _jsx(ToastStack, { toasts: toasts, onDismiss: dismiss })] }));
}
function ToastStack({ toasts, onDismiss, }) {
    if (toasts.length === 0)
        return null;
    return (_jsx("ol", { "data-toaster": "true", "aria-label": "Notifications", "data-position": "bottom-right", children: toasts.map((t) => (_jsx(ToastItem, { toast: t, onDismiss: onDismiss }, t.id))) }));
}
function variantIcon(variant) {
    switch (variant) {
        case 'success':
            return _jsx(CheckCircle2, { "aria-hidden": "true", size: 18 });
        case 'warning':
            return _jsx(AlertTriangle, { "aria-hidden": "true", size: 18 });
        case 'danger':
            return _jsx(OctagonX, { "aria-hidden": "true", size: 18 });
        case 'info':
        case 'default':
        default:
            return _jsx(Info, { "aria-hidden": "true", size: 18 });
    }
}
function ToastItem({ toast: t, onDismiss, }) {
    const variant = t.variant ?? 'default';
    const duration = t.durationMs ?? DEFAULT_DURATION_MS;
    const isUrgent = variant === 'warning' || variant === 'danger';
    return (_jsxs("li", { "data-toast": "true", "data-variant": variant, role: isUrgent ? 'alert' : 'status', "aria-live": isUrgent ? 'assertive' : 'polite', "aria-atomic": "true", children: [_jsx("span", { "data-toast-icon": "true", "data-variant": variant, children: variantIcon(variant) }), _jsxs("div", { "data-toast-body": "true", children: [_jsx("div", { "data-toast-title": "true", children: t.title }), t.description !== undefined ? (_jsx("div", { "data-toast-description": "true", children: t.description })) : null] }), _jsx("button", { type: "button", "data-toast-close": "true", "aria-label": "Dismiss notification", onClick: () => onDismiss(t.id), children: _jsx(X, { "aria-hidden": "true", size: 14 }) }), duration > 0 ? (_jsx("span", { "data-toast-progress": "true", "aria-hidden": "true", style: { animationDuration: `${String(duration)}ms` } })) : null] }));
}
/**
 * Hook into the Toaster. Throws if called outside `<Toaster>` so missing
 * setup is loud rather than silently no-op-ing toasts at the call site.
 */
export function useToast() {
    const ctx = useContext(ToastContext);
    if (ctx === null) {
        throw new Error('useToast must be called inside a <Toaster> provider');
    }
    return ctx;
}
//# sourceMappingURL=Toast.js.map